import React, {
    useCallback, useContext, useMemo, useState
} from 'react';
import _ from 'lodash';
import moment from 'moment';
import { TranslatorContext, IntlContext } from '@jutro/locale';
import { Button, Link } from '@jutro/components';
import { SgNumberUtil } from 'saga-modules';
import SgFailedDownloadPayment from 'sg-failed-download-payment';
import CepConstants from 'sg-cep-constants';
import SgNewCardDetails from 'sg-new-card-details';
import { SgCardService } from 'sg-capability-card';
import CommonConstants from 'sg-common-constants';
import SgScrollUtil from 'sg-scroll-util';
import messages from '../SgPolicyPaymentTile.messages';

function SgOutstandingPayments(props) {
    const {
        id,
        policyDetails,
        paymentDetails,
        setShowPaymentDetailsContent,
        setShowPaymentInstrument,
        showPaymentDetailsContent,
        arrearsPaid,
        setArrearsPaid,
        customerDetails
    } = props;

    const intl = useContext(IntlContext);
    const translator = useContext(TranslatorContext);

    const [showArrearsPaymentForm, setShowArrearsPaymentForm] = useState(false);
    const [showFailedDownloadMsg, setShowFailedDownloadMsg] = useState(false);

    const showPaymentForm = useCallback((shouldShowPaymentForm, event) => {
        if (event) {
            event.preventDefault();
        }
        setShowArrearsPaymentForm(shouldShowPaymentForm);
        setShowPaymentDetailsContent(!shouldShowPaymentForm);
        setArrearsPaid(false);
    }, [arrearsPaid, setArrearsPaid, setShowPaymentDetailsContent]);

    const overduePayments = useMemo(() => {
        const paymentsOverdue = _.filter(paymentDetails.invoiceSummary,
            (invoice) => invoice.invoiceStatus === CepConstants.PAID_STATUS.DUE
                && invoice.paidStatus === CepConstants.PAID_STATUS.UNPAID);
        return _.orderBy(paymentsOverdue, ['dueDate']);
    }, []);

    const overdueInvoiceAmount = useMemo(() => {
        const origAmount = overduePayments
            .map((invoice) => {
                return invoice.amountDue.amount;
            })
            .reduce((a, b) => {
                return a + b;
            }, 0);
        return Math.round(origAmount * 100) / 100;
    }, [overduePayments]);

    const postPay = useCallback((barclayResponse) => {
        const invoiceIds = overduePayments.map((invoice) => invoice.invoiceId);
        const policyNumber = paymentDetails.displayName;
        SgCardService.postPay(invoiceIds, barclayResponse, policyNumber, paymentDetails.internalId)
            .then(() => {
                setArrearsPaid(true);
            })
            .catch((err) => {
                const error = { error: err, customErrorMessage: 'Could not finish post payment actions' };
                throw error;
            });
    }, [overduePayments, paymentDetails, setArrearsPaid]);

    const arrearsPaymentForm = useMemo(() => {
        return (
            <div className="sg-policy-tile-expanding-details-container">
                <div className="sgCepPaymentInvoicesDetailsContainer">
                    <h2>
                        {translator(messages.InstalmentsToPay)}
                    </h2>
                    <table className="sgCepPaymentsTable">
                        <thead>
                            <tr>
                                <th>
                                    {translator(messages.PaymentDate)}
                                </th>
                                <th>
                                    {translator(messages.PaymentAmount)}
                                </th>
                                <th>
                                    {translator(messages.Status)}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            { overduePayments.map((invoice) => {
                                return (
                                    <tr>
                                        <td>
                                            {moment(invoice.dueDate).format('DD/MM/yyyy')}
                                        </td>
                                        <td>
                                            {SgNumberUtil.getCurrencyValue(intl, invoice.totalValue)}
                                        </td>
                                        <td>
                                            {CepConstants.PAID_STATUS.OVERDUE}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                    <p className="sgCepPaymentsTableTotalToPay">
                        {translator(messages.AmountToPay,
                            { amount: SgNumberUtil.getCurrencyValue(intl, { currency: 'gbp', amount: overdueInvoiceAmount }) })}
                    </p>
                    <SgFailedDownloadPayment showFailedDownloadMsg={showFailedDownloadMsg} />
                    {!showFailedDownloadMsg && (
                        <SgNewCardDetails
                            paymentDetails={paymentDetails}
                            customerDetails={customerDetails}
                            amount={overdueInvoiceAmount}
                            bind={postPay}
                            setShowFailedDownloadMsg={setShowFailedDownloadMsg}
                            isArrears
                            hideCPAQuestion={!policyDetails.basicInformation.cpaAvailableInArreas}
                        />
                    )}
                    <div className="sgBottomButtonsContainer">
                        <Link
                            href="/"
                            onClick={(event) => showPaymentForm(false, event)}
                        >
                            {translator(messages.Cancel)}
                        </Link>
                    </div>
                </div>
            </div>
        );
    }, [showFailedDownloadMsg]);

    const arrearsBanner = useMemo(() => {
        return (
            <div
                id="hasOutstandingPaymentSection"
                className="sg-payments-make-payment"
            >
                <h3>
                    {translator(messages.ArrearsHeader)}
                </h3>
                <div>
                    <Button
                        id="buttonMakeAPayment"
                        onClick={() => showPaymentForm(true)}
                        className="sgTransactionButton"
                    >
                        {translator(messages.Pay)}
                    </Button>
                </div>
            </div>
        );
    }, []);

    const confirmationMessage = useMemo(() => {
        SgScrollUtil.scrollToElementById(`policyPaymentContainer_${id}`, null, -50);
        return (
            <div className="sg-policy-tile-expanding-details-container">
                <div className="sgPaymentConfirmationMessageContainer">
                    <h2 id="confirmationMessage">
                        <i className="mir mi-check" />
                        {translator(messages.SuccessfulPayment)}
                    </h2>
                </div>
                { paymentDetails.paymentInstrument.paymentMethod !== CommonConstants.PAYMENT_METHOD_CODES.CHEQUE
                && (
                    <h3 id="updateCardOrBankMessage">
                        {translator(messages.UpdateDetailsNeeded)}
                    </h3>
                )}
                <div className="sgBottomButtonsContainer">
                    <Button
                        id="backToPayments"
                        onClick={() => { showPaymentForm(false); }}
                        className="sgButton sgSecondaryButton"
                    >
                        {translator(messages.BackToPayments)}
                    </Button>
                    { paymentDetails.paymentInstrument.paymentMethod !== CommonConstants.PAYMENT_METHOD_CODES.CHEQUE
                    && (
                        <Button
                            id="changePaymentDetails"
                            onClick={() => { showPaymentForm(false); setShowPaymentInstrument(true); }}
                            className="sgButton"
                        >
                            {translator(messages.ChangeMyPaymentDetails)}
                        </Button>
                    )}
                </div>
            </div>
        );
    }, []);

    return (
        <>
            {!arrearsPaid
                && (
                    <>
                        {showArrearsPaymentForm ? arrearsPaymentForm : arrearsBanner}
                    </>
                )

            }
            { arrearsPaid && !showPaymentDetailsContent && confirmationMessage }
        </>

    );
}

export default SgOutstandingPayments;
